import { Fragment, useState, useEffect } from "react";
import moment from "moment";
import { observer } from "mobx-react";
import { cancelScheduledNotification } from "../../api/new-api";
import { Alert, Snackbar } from "@mui/material";

type Props = {
  loading: boolean;
  error: string | null;
  data: { scheduledNotifications: any[] } | null;
  direction: string;
  refetch: () => void;
};

const ScheduledNotificationsContainer = observer(
  ({ loading, error, data, direction, refetch }: Props) => {
    const [upcomingNotifications, setUpcomingNotifications] = useState<any[]>(
      []
    );
    const [sentNotifications, setSentNotifications] = useState<any[]>([]);
    const [cancelledNotifications, setCancelledNotifications] = useState<any[]>(
      []
    );
    const [successOpen, setSuccessOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    useEffect(() => {
      if (data?.scheduledNotifications) {
        const upcoming = data.scheduledNotifications.filter(
          (n) => !n.isCancelled && !n.notificationSent
        );
        const sent = data.scheduledNotifications.filter(
          (n) => n.notificationSent
        );
        const cancelled = data.scheduledNotifications.filter(
          (n) => n.isCancelled
        );
        setUpcomingNotifications(upcoming);
        setSentNotifications(sent);
        setCancelledNotifications(cancelled);
      }
    }, [data]);

    const cancelNotification = async (scheduledNotificationId: string) => {
      try {
        if (
          window.confirm("Are you sure you want to cancel this notification?")
        ) {
          await cancelScheduledNotification({
            scheduledNotificationId: Number(scheduledNotificationId),
          });
          setSuccessMessage("Success: Notification cancelled.");
          setSuccessOpen(true);
        }
      } catch (error) {
        console.error("Error cancelling notification:", error);
      } finally {
        refetch();
      }
    };

    const renderNotification = (
      notification: any,
      status: string,
      color: string
    ) => (
      <div
        key={notification.scheduledNotificationId}
        className="mt-4 w-full bg-white"
      >
        <div
          className={`text-white text-center py-1 text-base font-normal`}
          style={{
            background: color,
          }}
        >
          {status}
        </div>
        <div className="border border-gray-200 shadow px-4 ">
          <div
            className="flex justify-between"
            style={{
              flexDirection: direction as "row" | "column",
            }}
          >
            <p className="mb-1 font-normal text-gray-700 dark:text-gray-400">
              <span className="font-normal">Schedule Type:</span>{" "}
              {getScheduleTypeString(notification.scheduleType)}
            </p>
            <p className="mb-1 font-normal text-gray-700 dark:text-gray-400">
              <span className="font-normal">Sending To:</span>{" "}
              {getMessageTypeString(
                notification.messageType,
                notification.criteria
              )}
            </p>
            <p className="mb-1 font-normal text-gray-700 dark:text-gray-400">
              <span className="font-normal">Scheduled At:</span>{" "}
              {moment(notification.timeScheduledAt).format(
                "MMMM Do YYYY, h:mm:ss a"
              )}
            </p>
          </div>

          <div className="mb-2 px-2 py-1 bg-[#f3f3f3] rounded-md">
            <h5 className="mb-2 text-md font-bold tracking-tight text-gray-900 dark:text-white">
              {notification.title}
            </h5>
            <p className="mb-3 text-md font-normal text-gray-700 dark:text-gray-400">
              {notification.message}
            </p>
          </div>
          {status === "UPCOMING" && (
            <button
              onClick={() =>
                cancelNotification(notification.scheduledNotificationId)
              }
              className="inline-flex items-center py-1 px-2 hover:opacity-75 my-1 text-sm font-normal text-center text-white bg-red-700 rounded-full"
            >
              Cancel Notification
            </button>
          )}
        </div>
      </div>
    );

    const getScheduleTypeString = (scheduleType: string) => {
      return scheduleType === "OnceInFuture" ? "Send Once Only" : "Unknown";
    };

    const getMessageTypeString = (messageType: string, criteria: string) => {
      if (messageType === "AllVenue") return "All Venue Members";
      if (messageType === "CustomFilter")
        return `All ${criteria.toUpperCase()} Members`;
      return "Unknown";
    };

    if (error) return <div>Error: {error}</div>;
    if (!loading && data?.scheduledNotifications?.length === 0) {
      return (
        <div className="text-center mt-10 text-gray-700 dark:text-gray-400">
          No notifications are scheduled
        </div>
      );
    }

    return (
      <div className="w-full">
        {upcomingNotifications.map((n) =>
          renderNotification(n, "UPCOMING", "#3494D5")
        )}
        {sentNotifications.map((n) => renderNotification(n, "SENT", "#2CC66D"))}
        {cancelledNotifications.map((n) =>
          renderNotification(n, "CANCELLED", "#E24A3B")
        )}
        <Snackbar
          open={successOpen}
          autoHideDuration={3000}
          onClose={() => setSuccessOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSuccessOpen(false)}
            severity="success"
            variant="filled"
          >
            {successMessage}
          </Alert>
        </Snackbar>
      </div>
    );
  }
);

export default ScheduledNotificationsContainer;
